import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '../components/ui/input-otp';
import { RoleDisplayNames, API_URL, UserRoles } from '../lib/utils';
import { toast } from 'sonner';
import { User, Mail, Shield, Key, Loader2, Pencil, X, Check, Smartphone, QrCode, Copy, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const Settings = () => {
  const { user, changePassword, refreshUser } = useAuth();
  usePageTitle('Settings');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Profile edit state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || ''
  });
  
  // 2FA state
  const [totpStatus, setTotpStatus] = useState({ totp_enabled: false, backup_codes_remaining: 0 });
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showBackupCodesDialog, setShowBackupCodesDialog] = useState(false);
  const [setupStep, setSetupStep] = useState('qr'); // 'qr' | 'verify' | 'backup'
  const [qrCode, setQrCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [totpLoading, setTotpLoading] = useState(false);
  const [showManualKey, setShowManualKey] = useState(false);
  
  // Check if user can edit profile (Admin or Training Admin only)
  const canEditProfile = user?.role === UserRoles.ADMIN || user?.role === UserRoles.TRAINING_ADMIN;

  // Fetch 2FA status on mount
  useEffect(() => {
    fetchTotpStatus();
  }, []);

  const fetchTotpStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/totp/status`);
      setTotpStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    if (!/[A-Z]/.test(newPassword)) {
      toast.error('Password must contain at least one uppercase letter');
      return;
    }
    
    if (!/[a-z]/.test(newPassword)) {
      toast.error('Password must contain at least one lowercase letter');
      return;
    }
    
    if (!/\d/.test(newPassword)) {
      toast.error('Password must contain at least one digit');
      return;
    }

    setLoading(true);
    
    const result = await changePassword(currentPassword, newPassword);
    
    if (result.success) {
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      toast.error('Failed to change password', {
        description: result.error
      });
    }
    
    setLoading(false);
  };

  const handleStartEditing = () => {
    setProfileForm({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || ''
    });
    setIsEditingProfile(true);
  };

  const handleCancelEditing = () => {
    setIsEditingProfile(false);
    setProfileForm({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || ''
    });
  };

  const handleSaveProfile = async () => {
    if (!profileForm.first_name || !profileForm.last_name || !profileForm.email) {
      toast.error('Please fill in all profile fields');
      return;
    }

    setProfileLoading(true);
    
    try {
      await axios.put(`${API_URL}/auth/profile`, {
        first_name: profileForm.first_name,
        last_name: profileForm.last_name,
        email: profileForm.email
      });
      
      toast.success('Profile updated successfully');
      setIsEditingProfile(false);
      window.location.reload();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update profile';
      toast.error(errorMessage);
    } finally {
      setProfileLoading(false);
    }
  };

  // 2FA Setup
  const handleSetup2FA = async () => {
    setTotpLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/totp/setup`);
      setQrCode(response.data.qr_code);
      setTotpSecret(response.data.secret);
      setSetupStep('qr');
      setShowSetupDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup 2FA');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setTotpLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/totp/verify`, {
        code: verifyCode,
        secret: totpSecret
      });
      setBackupCodes(response.data.backup_codes);
      setSetupStep('backup');
      toast.success('2FA enabled successfully!');
      fetchTotpStatus();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setTotpLoading(true);
    try {
      await axios.post(`${API_URL}/auth/totp/disable`, { code: disableCode });
      toast.success('2FA disabled successfully');
      setShowDisableDialog(false);
      setDisableCode('');
      fetchTotpStatus();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    const code = prompt('Enter your 6-digit authenticator code to regenerate backup codes:');
    if (!code || code.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setTotpLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/totp/regenerate-backup-codes`, { code });
      setBackupCodes(response.data.backup_codes);
      setShowBackupCodesDialog(true);
      toast.success('Backup codes regenerated');
      fetchTotpStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setTotpLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const closeSetupDialog = () => {
    setShowSetupDialog(false);
    setVerifyCode('');
    setQrCode('');
    setTotpSecret('');
    setSetupStep('qr');
    setShowManualKey(false);
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Settings
        </h1>
        <p className="text-slate-500 mt-1">
          Manage your account settings
        </p>
      </div>

      {/* Profile Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Profile Information
              </CardTitle>
              <CardDescription>Your account details</CardDescription>
            </div>
            {canEditProfile && !isEditingProfile && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleStartEditing}
                data-testid="edit-profile-btn"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
            )}
            {canEditProfile && isEditingProfile && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleCancelEditing} disabled={profileLoading}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button 
                  size="sm"
                  className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                  onClick={handleSaveProfile}
                  disabled={profileLoading}
                  data-testid="save-profile-btn"
                >
                  {profileLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Save
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isEditingProfile ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={profileForm.first_name}
                    onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                    data-testid="profile-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={profileForm.last_name}
                    onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                    data-testid="profile-lastname-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email"><Mail className="w-4 h-4 inline mr-1" />Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                  data-testid="profile-email-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-500 text-sm"><Shield className="w-4 h-4 inline mr-1" />Role</Label>
                <p className="font-medium text-slate-500">{RoleDisplayNames[user?.role] || user?.role}</p>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-slate-500 text-sm">First Name</Label>
                  <p className="font-medium">{user?.first_name}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-500 text-sm">Last Name</Label>
                  <p className="font-medium">{user?.last_name}</p>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-500 text-sm"><Mail className="w-4 h-4 inline mr-1" />Email</Label>
                <p className="font-medium">{user?.email}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-500 text-sm"><Shield className="w-4 h-4 inline mr-1" />Role</Label>
                <p className="font-medium">{RoleDisplayNames[user?.role] || user?.role}</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account using Google Authenticator
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {totpStatus.totp_enabled ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-green-800">2FA is enabled</p>
                  <p className="text-sm text-green-600">
                    {totpStatus.backup_codes_remaining} backup codes remaining
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={handleRegenerateBackupCodes}
                  disabled={totpLoading}
                  data-testid="regenerate-backup-codes-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Regenerate Backup Codes
                </Button>
                <Button 
                  variant="outline" 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setShowDisableDialog(true)}
                  data-testid="disable-2fa-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Disable 2FA
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-amber-800">2FA is not enabled</p>
                  <p className="text-sm text-amber-600">
                    We recommend enabling 2FA for enhanced security
                  </p>
                </div>
              </div>
              
              <Button 
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                onClick={handleSetup2FA}
                disabled={totpLoading}
                data-testid="enable-2fa-btn"
              >
                {totpLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <QrCode className="w-4 h-4 mr-2" />}
                Enable Two-Factor Authentication
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Change Password
          </CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current">Current Password</Label>
              <Input
                id="current"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                data-testid="settings-current-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new">New Password</Label>
              <Input
                id="new"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                data-testid="settings-new-password"
              />
              <p className="text-xs text-slate-500">
                Must be at least 8 characters with uppercase, lowercase, and a number
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm New Password</Label>
              <Input
                id="confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                data-testid="settings-confirm-password"
              />
            </div>
            <Button 
              type="submit" 
              className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
              disabled={loading}
              data-testid="settings-change-password-btn"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Changing...</> : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* 2FA Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={closeSetupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              {setupStep === 'qr' && 'Scan this QR code with Google Authenticator, Authy, or Microsoft Authenticator'}
              {setupStep === 'verify' && 'Enter the 6-digit code from your authenticator app'}
              {setupStep === 'backup' && 'Save these backup codes in a secure location'}
            </DialogDescription>
          </DialogHeader>

          {setupStep === 'qr' && (
            <div className="space-y-4">
              {qrCode && (
                <div className="flex justify-center p-4 bg-white border rounded-lg">
                  <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
                </div>
              )}
              
              <Button variant="outline" onClick={() => setShowManualKey(!showManualKey)} className="w-full">
                {showManualKey ? 'Hide manual key' : "Can't scan? Enter manually"}
              </Button>
              
              {showManualKey && totpSecret && (
                <div className="p-3 bg-slate-100 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Manual entry key:</p>
                  <div className="flex items-center justify-between">
                    <code className="text-sm font-mono break-all">{totpSecret}</code>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(totpSecret)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              <Button className="w-full bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white" onClick={() => setSetupStep('verify')}>
                Next: Verify Code
              </Button>
            </div>
          )}

          {setupStep === 'verify' && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={verifyCode} onChange={setVerifyCode}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setSetupStep('qr')} className="flex-1">
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                  onClick={handleVerify2FA}
                  disabled={verifyCode.length !== 6 || totpLoading}
                >
                  {totpLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Verify & Enable
                </Button>
              </div>
            </div>
          )}

          {setupStep === 'backup' && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Save these codes now!
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  You won't be able to see them again. Use them if you lose access to your authenticator.
                </p>
              </div>
              
              <div className="p-3 bg-slate-100 rounded-lg space-y-1 font-mono text-sm">
                {backupCodes.map((code, i) => (
                  <div key={i} className="flex justify-between items-center">
                    <span>{code}</span>
                  </div>
                ))}
              </div>
              
              <Button variant="outline" onClick={() => copyToClipboard(backupCodes.join('\n'))} className="w-full">
                <Copy className="w-4 h-4 mr-2" />
                Copy All Codes
              </Button>
              
              <Button className="w-full bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white" onClick={closeSetupDialog}>
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enter your authenticator code to disable 2FA. This will make your account less secure.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={disableCode} onChange={setDisableCode}>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowDisableDialog(false); setDisableCode(''); }} className="flex-1">
                Cancel
              </Button>
              <Button 
                variant="destructive"
                className="flex-1"
                onClick={handleDisable2FA}
                disabled={disableCode.length !== 6 || totpLoading}
              >
                {totpLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Disable 2FA
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodesDialog} onOpenChange={setShowBackupCodesDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Backup Codes</DialogTitle>
            <DialogDescription>
              Your old backup codes have been invalidated. Save these new codes securely.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-slate-100 rounded-lg space-y-1 font-mono text-sm">
              {backupCodes.map((code, i) => (
                <div key={i}>{code}</div>
              ))}
            </div>
            
            <Button variant="outline" onClick={() => copyToClipboard(backupCodes.join('\n'))} className="w-full">
              <Copy className="w-4 h-4 mr-2" />
              Copy All Codes
            </Button>
            
            <Button className="w-full" onClick={() => setShowBackupCodesDialog(false)}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
